"use client";
import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { api } from "../lib/api";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const intakeSchema = z.object({
  whatBringsYouInToday: z.string().min(3, "Please describe what brings you in today."),
  medicationInfo: z.string().optional(),
  brokenSkin: z.enum(["yes", "no"]),
  brokenSkinDescription: z.string().optional(),
  pressureLevel: z.enum(["Light", "Medium", "Firm"]),
  additionalNotes: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.brokenSkin === "yes" && (!data.brokenSkinDescription || data.brokenSkinDescription.trim().length < 3)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please describe the broken skin or injury areas.",
      path: ["brokenSkinDescription"],
    });
  }
});

export default function IntakeForm({
  clientEmail,
  clientName,
  bookingId,
  onComplete,
}) {
  const [initialLoading, setInitialLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const form = useForm({
    resolver: zodResolver(intakeSchema),
    defaultValues: {
      whatBringsYouInToday: "",
      medicationInfo: "",
      brokenSkin: "no",
      brokenSkinDescription: "",
      pressureLevel: "Medium",
      additionalNotes: "",
    },
  });

  const brokenSkinValue = form.watch("brokenSkin");

  // Fetch existing intake form on mount
  useEffect(() => {
    const fetchExistingIntake = async () => {
      if (!clientEmail) return;

      try {
        setInitialLoading(true);
        const data = await api.intake.get({ email: clientEmail }).catch(() => null);
        
        if (data?.intakeForm) {
          const intake = data.intakeForm;
          form.reset({
            whatBringsYouInToday: intake.whatBringsYouInToday || "",
            medicationInfo: intake.medicationInfo || "",
            brokenSkin: intake.brokenSkin ? "yes" : "no",
            brokenSkinDescription: intake.brokenSkinDescription || "",
            pressureLevel: intake.pressureLevel || "Medium",
            additionalNotes: intake.additionalNotes || "",
          });
        }
      } catch (err) {
        console.warn("Error fetching existing intake form:", err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchExistingIntake();
  }, [clientEmail, form]);

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setError("");

      await api.intake.create({
        clientEmail,
        clientName,
        bookingId,
        whatBringsYouInToday: values.whatBringsYouInToday.trim(),
        medicationInfo: values.medicationInfo?.trim() || undefined,
        brokenSkin: values.brokenSkin === "yes",
        brokenSkinDescription: values.brokenSkin === "yes" ? values.brokenSkinDescription?.trim() : undefined,
        pressureLevel: values.pressureLevel,
        additionalNotes: values.additionalNotes?.trim() || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1500);
    } catch (err) {
      setError(err?.message || "Failed to save intake form. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-4" />
        <p className="text-sm text-slate-500 font-medium">Retrieving your health profile...</p>
      </div>
    );
  }

  if (success) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center py-20 text-center"
      >
        <div className="bg-emerald-100 p-4 rounded-full mb-6">
          <CheckCircle2 className="h-12 w-12 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Information Saved!</h3>
        <p className="text-slate-500">Your health intake form has been updated successfully.</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-3 border-b border-slate-100 pb-6">
        <div className="bg-emerald-50 p-2 rounded-lg">
          <AlertCircle className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Health Intake Form</h3>
          <p className="text-sm text-slate-500">Please provide session-related health details</p>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex gap-3 text-sm animate-in fade-in slide-in-from-top-2">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 gap-8">
            {/* What brings you in */}
            <FormField
              control={form.control}
              name="whatBringsYouInToday"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What brings you in today?</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="E.g. Upper back tension, stress relief, specific injury..."
                      className="min-h-[120px] bg-slate-50/30 border-slate-200 focus:bg-white resize-none"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Medications */}
            <FormField
              control={form.control}
              name="medicationInfo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Are you currently taking any medications? (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="List any relevant medications..."
                      className="min-h-[80px] bg-slate-50/30 border-slate-200 focus:bg-white resize-none"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              {/* Broken Skin */}
              <FormField
                control={form.control}
                name="brokenSkin"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Any broken skin or injury areas?</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex gap-4"
                      >
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="no" />
                          </FormControl>
                          <FormLabel className="font-normal">No</FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="yes" />
                          </FormControl>
                          <FormLabel className="font-normal">Yes</FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Pressure Level */}
              <FormField
                control={form.control}
                name="pressureLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Pressure Level</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-slate-50/30 border-slate-200">
                          <SelectValue placeholder="Select pressure" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Light">Light</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Firm">Firm</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Broken Skin Description - Conditional */}
            {brokenSkinValue === "yes" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <FormField
                  control={form.control}
                  name="brokenSkinDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description of issues/injuries</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Please specify areas to avoid or focus on..."
                          className="min-h-[100px] bg-slate-50/30 border-slate-200 focus:bg-white resize-none"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </motion.div>
            )}

            {/* Additional Notes */}
            <FormField
              control={form.control}
              name="additionalNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Comments (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Anything else your therapist should know?"
                      className="min-h-[80px] bg-slate-50/30 border-slate-200 focus:bg-white resize-none"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="pt-4 border-top border-slate-100">
            <Button 
              type="submit" 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-600/20 transition-all active:scale-[0.98]"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Form...
                </>
              ) : (
                "Save Health Intake Form"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </motion.div>
  );
}
